import classNames from "classnames";
import {isValid, parseISO} from "date-fns";
import {Link} from "react-router";

import Button from "@/components/button/Button.tsx";
import {ChecksModalButton} from "@/components/ChecksModalButton/ChecksModalButton";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {dateStringWithTimezone, timeStringWithTimezone} from "@/components/datetime/utils.ts";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {MaintenanceAuditEventsPageResult} from "@/lib/queries/maintenance.ts";
import {MaintenanceEvent} from "@/lib/types/maintenance.ts";
import {MaintenanceEventTypePill} from "@/pages/maintenance/maintenance-dashboard/tabs/schedule-eventlog/EventLogTable/components/MaintenanceEventTypePill.tsx";
import {
  getEventDetailsDuration,
  getEventDetailsName,
} from "@/pages/maintenance/maintenance-dashboard/tabs/schedule-eventlog/EventLogTable/EventLogTable.utils.ts";

import styles from "./EventLogTable.module.scss";

type EventLogTableProps = {
  data: MaintenanceAuditEventsPageResult["maintenanceAuditEvents"];
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  pagination: LimitAndOffset;
  setPagination: (pagination: LimitAndOffset) => void;
  isFiltered?: boolean;
  loading?: boolean;
};

export const EventLogTable = ({
  data,
  sortColumn,
  sortDirection,
  onSortingChange,
  pagination,
  setPagination,
  isFiltered,
  loading,
}: EventLogTableProps) => {
  const {user} = useCurrentUserContext();

  const specs: ColumnSpec<MaintenanceEvent>[] = [
    {
      fieldName: "created_at",
      fieldTitle: "Time",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.timeColumn),
      },
      accessor: (event: MaintenanceEvent) => {
        const dateObj = parseISO(event.created_at);
        if (!isValid(dateObj)) return null;
        return (
          <div className={"pulseTextBodySmall"}>
            {dateStringWithTimezone(dateObj, user?.account.timezone, user?.current_date_format)}{" "}
            {timeStringWithTimezone(dateObj, user?.account.timezone, user?.current_time_format)}
          </div>
        );
      },
    },
    {
      fieldName: "event_type",
      fieldTitle: "Event",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.eventColumn),
      },
      accessor: (event: MaintenanceEvent) => <MaintenanceEventTypePill eventType={event.event_type} />,
    },
    {
      fieldName: "details",
      fieldTitle: "Details",
      meta: {
        headerClassName: classNames("text-nowrap", styles.detailsColumn),
      },
      accessor: (event: MaintenanceEvent) => {
        const name = getEventDetailsName(event);
        const duration = getEventDetailsDuration(event);
        return (
          <Tooltip tooltipContent={name} delayMs={500}>
            <span className="d-flex flex-column w-fit-content">
              <div className="ellipsis-overflow pulseTextBodySmallBold pulse-color-gray-900">{name}</div>
              {duration && <div className="pulseTextBodyTiny pulse-color-gray-700">{duration}</div>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      fieldName: "services",
      fieldTitle: "Affected Checks",
      enableSorting: true,
      meta: {
        headerClassName: classNames(styles.checksColumn),
      },
      accessor: (event: MaintenanceEvent) => {
        const services = event.services_at_creation ?? [];

        if (services.length > 0) {
          return (
            <ChecksModalButton
              auditEventId={event.id}
              numberOfChecks={services.length}
              title={"Schedule: " + (event.schedule?.name ?? "")}
            >
              {services.length}
            </ChecksModalButton>
          );
        }

        return <span className="px-3">0</span>;
      },
    },
  ];

  return (
    <>
      <Table
        rows={data.items}
        specs={specs}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        isLoading={loading}
        noDataComponent={
          <EmptyTable colSpan={specs.length}>
            {isFiltered ? (
              <div className="d-flex">No maintenance events found matching the filter(s) above.</div>
            ) : (
              <div className="d-flex flex-column align-items-center gap-3 py-5">
                <div className="pulse-color-gray-800 pulseTextBodyBold">No previous Maintenance events.</div>
                <Button variant={"secondary"} size={"large"} as={Link} to={"/maintenance/schedules"}>
                  View Schedules
                </Button>
              </div>
            )}
          </EmptyTable>
        }
        minWidth={"736px"}
        fixedWidth={true}
        pagination={pagination}
        updatePagination={setPagination}
        paginationData={data.items}
        totalRows={data.total}
      />
    </>
  );
};
